import { Group, Paper, Popover, Title } from '@mantine/core';
import { featureCollection } from '@turf/turf';
import { MapEvent } from 'mapbox-gl';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SamplePopup, SamplePopupData } from '../../components/sample-popup';
import { SamplingPlansFiltersMenu } from '../../components/sampling-plans-filters-menu/filters';
import { SamplingPlansFiltersButton } from '../../components/sampling-plans-filters-menu/filters-button';
import { useSamplingQueries } from '../../data-access/queries/sampling';
import { flattenSamples } from '../../utils/flatten-samples';

import {
  PfMap,
  SamplesLayerProps,
  SamplesLayers,
} from '@map';
import { useFiltersStore } from '@data-access';
import { FullMainHeight } from '@ui';

export const SamplingPlansMapPage: FC = () => {
  const { t } = useTranslation();
  const [popupData, setPopupData] = useState<SamplePopupData | null>(null);
  const [selectedSampleId, setSelectedSampleId] = useState<string | null>(null);

  const filters = useFiltersStore.use['samplingPlansMap']().filters;
  const samplingQueries = useSamplingQueries();

  // Use orders filters endpoint as fallback, or create a specific one later
  const filtersQuery = useQuery({
    ...samplingQueries.orders.filters(),
    placeholderData: keepPreviousData,
  });

  const mapsData = useQuery({
    ...samplingQueries.plans.maps({
      page: 1,
      sorting: [],
      filters,
    }),
    placeholderData: keepPreviousData,
  });

  // Flatten all samples from all plans
  const allSamples = useMemo(() => {
    if (!mapsData.data) return [];
    return mapsData.data.flatMap((plan) => plan.samples);
  }, [mapsData.data]);

  const flattenedPoints = useMemo(() => {
    const points = flattenSamples(allSamples);
    // Add isSelected property to points from the selected sample
    return points.map((point) => ({
      ...point,
      properties: {
        ...point.properties,
        isSelected: selectedSampleId !== null && point.properties.sampleId === selectedSampleId,
      },
    }));
  }, [allSamples, selectedSampleId]);

  const handleLoad = (map: MapEvent) => {
    if (flattenedPoints.length > 0) {
      // Calculate bounds from all points
      const coordinates = flattenedPoints
        .map((point) => point.geometry.coordinates as [number, number])
        .filter((coord) => coord && coord.length === 2);

      if (coordinates.length > 0) {
        const lngs = coordinates.map((c) => c[0]);
        const lats = coordinates.map((c) => c[1]);
        const bounds = {
          north: Math.max(...lats),
          south: Math.min(...lats),
          east: Math.max(...lngs),
          west: Math.min(...lngs),
        };

        map.target.fitBounds(
          [
            [bounds.west, bounds.south],
            [bounds.east, bounds.north],
          ],
          {
            padding: 50,
          },
        );
      }
    }
  };

  const handleMarkerClick: SamplesLayerProps['onClick'] = ({
    id: featureId,
    lngLat,
  }) => {
    // Find the parent sample that contains this point
    const parentSample = allSamples.find((s) =>
      s.points.features.some((p) => p.id === featureId),
    );

    if (parentSample) {
      // Set selected sample ID to highlight all points from the same sample
      setSelectedSampleId(parentSample.id);

      // Find the specific point for popup data
      const point = parentSample.points.features.find((p) => p.id === featureId);

      if (point) {
        setPopupData({
          longitude: lngLat.lng,
          latitude: lngLat.lat,
          code: point.properties.code || '',
          status: point.properties.status || null,
          fieldName: point.properties.field_name || null,
          carbonContent: parentSample?.carbon_content || null,
          soilDensity: parentSample?.soil_density || null,
        });
      }
    }
  };

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Group justify="space-between">
            <Group>
              <Title order={2}>{t('app.nav.samplingPlansMap')}</Title>
              <Popover position="bottom-start" width={400}>
                <Popover.Target>
                  <SamplingPlansFiltersButton />
                </Popover.Target>
                <Popover.Dropdown>
                  {filtersQuery.data ? (
                    <SamplingPlansFiltersMenu filters={filtersQuery.data} />
                  ) : null}
                </Popover.Dropdown>
              </Popover>
            </Group>
          </Group>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <Paper h="100%" p={0} radius="sm" withBorder>
          <PfMap onLoad={handleLoad}>
            {flattenedPoints.length > 0 ? (
              <SamplesLayers
                data={featureCollection(flattenedPoints)}
                onClick={handleMarkerClick}
                size={30}
                withLabel
              />
            ) : null}

            {popupData && (
              <SamplePopup
                data={popupData}
                onClose={() => {
                  setPopupData(null);
                  setSelectedSampleId(null);
                }}
              />
            )}
          </PfMap>
        </Paper>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
};
